/*
 * Keep an agent chat in step with teammates' tabs. Events from the shared
 * stream (`RealtimeProvider`) update the thread list, the agent-turn lock and
 * the transcript; while the stream is down the same data is polled instead.
 * `owner` names the view claiming the scope (`"case-ide"`, `"agent-panel"`)
 * so several chats can be live on one connection.
 */
import { useEffect, useRef } from "react";

import {
  useRealtime,
  useRealtimeEvent,
  useRealtimeScope
} from "@/lib/realtime/RealtimeProvider";

import { useThreadAttendance } from "../agentAttention/hooks/useThreadAttendance";

import type { AgentThread } from "./api/agentApi";
import type { AgentChat, AgentUIMessage } from "./useAgentChat";

/** Poll cadence when the stream is not open. */
export const FALLBACK_POLL_MS = 10_000;

const latestNs = (messages: AgentUIMessage[]): number | null => {
  let max: number | null = null;
  for (const m of messages) {
    const ns = (m as { created_at_ns?: unknown }).created_at_ns;
    if (typeof ns === "number" && (max === null || ns > max)) max = ns;
  }
  return max;
};

/** Does a thread event belong to this chat's scope? Legacy case threads may
 * carry only `individual_id`. */
const inScope = (
  data:
    | {
        scope_kind?: string;
        scope_id?: string | null;
        individual_id?: string | null;
      }
    | null
    | undefined,
  scope: AgentChat["scope"]
): boolean => {
  if (!data) return false;
  if (data.scope_kind && data.scope_id)
    return data.scope_kind === scope.kind && data.scope_id === scope.id;
  if (scope.kind === "case" && data.individual_id)
    return data.individual_id === scope.id;
  // No scope on the event (older payloads): trust it only for the case scope.
  return scope.kind === "case" && !data.scope_kind;
};

export const useAgentThreadLive = (owner: string, chat: AgentChat): void => {
  const { scope } = chat;
  const { status } = useRealtime();
  const chatRef = useRef(chat);
  chatRef.current = chat;
  // Set when a message could not be merged (this client was streaming); the
  // transcript is reloaded once the turn ends so nothing is lost.
  const missed = useRef(false);

  useThreadAttendance(scope, chat.threadId);

  useRealtimeScope(owner, {
    kind: scope.kind,
    scopeId: scope.id,
    threadId: chat.threadId,
    afterNs: latestNs(chat.messages)
  });

  useRealtimeEvent<AgentThread>("thread.updated", (thread) => {
    if (!inScope(thread, chatRef.current.scope)) return;
    chatRef.current.applyThreadUpdate(thread);
  });
  useRealtimeEvent<{
    id: string;
    individual_id?: string | null;
    scope_kind?: string;
    scope_id?: string | null;
  }>("thread.deleted", (data) => {
    if (!inScope(data, chatRef.current.scope)) return;
    if (data?.id) chatRef.current.removeThread(data.id);
  });
  useRealtimeEvent<{ thread_id: string; message: AgentUIMessage }>(
    "message.upserted",
    (data) => {
      if (!data?.thread_id || !data.message) return;
      if (data.thread_id !== chatRef.current.threadId) return;
      const ok = chatRef.current.applyMessageUpsert(
        data.thread_id,
        data.message
      );
      if (!ok) missed.current = true;
    }
  );
  useRealtimeEvent<{ thread_id: string; id: string }>(
    "message.deleted",
    (data) => {
      if (!data?.thread_id || !data.id) return;
      if (data.thread_id !== chatRef.current.threadId) return;
      chatRef.current.removeMessage(data.id);
    }
  );

  // Our own turn finished with teammates' messages queued behind it.
  const chatStatus = chat.status;
  useEffect(() => {
    if (!missed.current) return;
    if (chatStatus !== "ready" && chatStatus !== "error") return;
    missed.current = false;
    void chatRef.current.reloadMessages().catch(() => {});
  }, [chatStatus]);

  // Stream gap: the server replays messages via `Last-Event-ID`, but a thread
  // edit (lock, title) has no cursor, so refresh the list on every reopen.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (status === "open") {
      if (wasOpen.current) void chatRef.current.refreshThreads();
      wasOpen.current = true;
    }
  }, [status]);

  // Polling fallback while the stream is closed or still (re)connecting. Not
  // while paused: a hidden tab catches up from its cursor when shown.
  useEffect(() => {
    if (status !== "closed" && status !== "connecting") return undefined;
    const tick = () => {
      const c = chatRef.current;
      void c.refreshThreads();
      void c.reloadMessages().catch(() => {});
    };
    const timer = setInterval(tick, FALLBACK_POLL_MS);
    return () => clearInterval(timer);
  }, [status]);
};
